import { ROLES } from "@infinitaslearning/module-he-common";
import { Collapsible } from "components/Collapsible";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getAnalyticsTracker, setAnalyticsPage } from "analytics";
import { AdditionalsTestRow } from "components/AdditionalsTestRow";
import { StyleParagraph } from "components/AdditionalsTestRow/AdditionalsTestRow.styled";
import { DownloadButton } from "components/DownloadButton";
import { ExtraMaterial } from "components/ExtraMaterial";
import { Player } from "components/Player";
import { ASSETS_TYPES } from "constants/index";
import { useBook } from "hooks/useBook";
import { useProfile } from "hooks/useProfile";
import { useTest } from "hooks/useTest";
import { Footer } from "layout/Footer";
import { HeaderMenu } from "layout/HeaderMenu";
import type { PathParams } from "models/path/Path";
import { getAccountRole } from "persistence/access";
import {
  CollapsibleContainer,
  Container,
  FlashCardStyled,
  StyleContainer,
  StyleTitle,
  TestsContainer,
} from "./StudentOverview.styled";

export const StudentOverview: React.FC = observer(() => {
  const { additionalsId }: PathParams = useParams();
  const role = getAccountRole();
  const { bookInfo, assetsInfo, fetchBook, fetchOneBookAssets } = useBook();
  const { t } = useTranslation("material");
  const { fetchLicenses } = useProfile();
  const { testResultsInfo, fetchTestResults } = useTest();

  useEffect(() => {
    if (getAnalyticsTracker()) {
      setAnalyticsPage(additionalsId, "overview", additionalsId);
    }
  }, [additionalsId]);

  useEffect(() => {
    role === ROLES.STUDENT && additionalsId !== "courses" && fetchLicenses(additionalsId);
  }, [fetchLicenses, additionalsId, role]);

  useEffect(() => {
    fetchBook(additionalsId);
  }, [fetchBook, additionalsId]);

  useEffect(() => {
    fetchOneBookAssets(additionalsId);
  }, [fetchOneBookAssets, additionalsId]);

  useEffect(() => {
    role === ROLES.STUDENT && fetchTestResults();
  }, [role, fetchTestResults]);

  const flashCards = assetsInfo?.filter((a) => a.type === "flashcard");
  const extraMaterial = assetsInfo?.filter((a) => a.type !== "flashcard" && a.role === "student");
  const getResult = (chapterId: string): number =>
    testResultsInfo.find((result) => result.chapterId === chapterId)?.result || 0;

  return (
    <Container>
      <HeaderMenu dataCy="header-menu" />
      <StyleContainer>
        <div style={{ float: "left" }}>
          <StyleTitle data-cy="book-title">
            {t("additionals.additionalMaterial")}
            {bookInfo?.title}
          </StyleTitle>
          <StyleParagraph>{t("additionals.titleInfo")}</StyleParagraph>
        </div>
        <br />
        {bookInfo?.chapters?.length > 0 &&
          bookInfo?.chapters?.some((chapter) => chapter?.hasExam) && (
            <CollapsibleContainer>
              <Collapsible title={t("additionals.test")}>
                <TestsContainer>
                  {bookInfo?.chapters?.map(
                    (chapter, i) =>
                      chapter.hasExam && (
                        <AdditionalsTestRow
                          key={chapter.id}
                          chapter={chapter}
                          index={i}
                          result={getResult(chapter.id)}
                        />
                      ),
                  )}
                </TestsContainer>
              </Collapsible>
            </CollapsibleContainer>
          )}
        {flashCards?.length > 0 && (
          <CollapsibleContainer>
            <Collapsible title={t("additionals.flashCards")}>
              <FlashCardStyled>
                {flashCards?.map((flashCard) => (
                  <div key={flashCard.id}>
                    <Player dataCy="flashCard-player-item" initialData={flashCard.content} />
                  </div>
                ))}
              </FlashCardStyled>
            </Collapsible>
          </CollapsibleContainer>
        )}
        {extraMaterial.length > 0 && (
          <CollapsibleContainer>
            <Collapsible
              headerComponent={
                <DownloadButton
                  documents={extraMaterial?.filter((a) => a.type === ASSETS_TYPES.document)}
                />
              }
              title={t("additionals.extraMaterial")}
            >
              <ExtraMaterial assets={extraMaterial} />
            </Collapsible>
          </CollapsibleContainer>
        )}
      </StyleContainer>
      <Footer />
    </Container>
  );
});
